import React, { useState, useEffect } from 'react';
import api from './api/axiosInstance';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Container, Alert, Form, Button, Modal, Image } from 'react-bootstrap';
import CountryGeneralInfo from './CountryGeneralInfo.js';
import NavTabs from './NavTabs';
import Breadcrumb from './Breadcrumb';
import Sidebar from './Sidebar.js';
import { TicketCountContext } from './TicketCountContext';
import icSucess from './assets/img/ic-celebration-circle.svg';
import CountryFinancialsBanks from './CountryFinancialsBanks';
import { exportCountryPdf } from './api/exportCountryPdf';

const CountryEditFinancialsBanks = () => {
    
    const { id } = useParams(); // country _id from URL
    const navigate = useNavigate();

    const { ticketCount } = React.useContext(TicketCountContext);
    
    const [formData, setFormData] = useState({
        country: { id: '', name: '' },
        url: '',
        bankAccountDocuments: '',
        exchangeRate: '',
        currency: '',
    });

    const [errors, setErrors] = useState({}); // State for validation errors
    const [loading, setLoading] = useState(true); // ✅ Show spinner while fetching

    const [showErrorMessage, setShowErrorMessage] = useState(false);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showSavedModal, setShowSavedModal] = useState(false);

    const handleExportPdf = async () => {
        try {
            await exportCountryPdf(id);
        } catch (error) {
            console.error('Error exporting country PDF:', error);
            alert('An error occurred while exporting the country report.');
        }
    };

    const handleDeleteCountry = () => {
        setShowDeleteModal(true);
    };

    useEffect(() => {
        const fetchCountry = async () => {
            try {
                const response = await api.get(`/api/countries/${id}`);

                const data = response.data;

                // ✅ Ensure fallback/defaults are safe
                setFormData({
                    country: data.country || { id: '', name: '' },
                    url: data.url || '',
                    bankAccountDocuments: data.bankAccountDocuments || '',
                    exchangeRate: data.exchangeRate || '',
                    currency: data.currency || '',
                });

                setLoading(false); // ✅ Done loading
            } catch (error) {
                console.error('Error fetching country:', error);
                setLoading(false); // ✅ Avoid infinite spinner
            }
        };

        fetchCountry();
    }, [id]);

    const confirmDeleteCountry = async () => {
        try {
            await api.delete(`/api/countries/${id}`);
            setShowDeleteModal(false);
            navigate('/onboarding-country-list'); // Redirect to company list after deletion
        } catch (error) {
            console.error('Error deleting country:', error);
            alert('An error occurred while deleting the country.');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        console.log('handleChange called with:', name, value);

        // Check if the incoming change is for the country object and handle accordingly
        if (name === 'country' && typeof value === 'object') {
            setFormData(prevData => ({
                ...prevData,
                country: { ...value }  // Update the country object properly
            }));
        } else {
            setFormData(prevData => ({
                ...prevData,
                [name]: value  // This assumes value is always correct
            }));
        }
    };

    const handleGeneralSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await api.patch(`/api/countries/${id}`, formData);
            console.log('Updated country:', response.data);
            setShowSavedModal(true);
        } catch (error) {
            console.error('Error updating country:', error);
        }
    };

    const countryNavItems = [
        { to: '/country-edit-general/:id', eventKey: 'countryInfo', label: 'General' },
        { to: '/country-edit-visa-stay/:id', eventKey: 'visaStay', label: 'Visa & Stay' },
        { to: '/country-edit-work-tax/:id', eventKey: 'countryTax', label: 'Work & Tax' },
        { to: '/country-edit-financials-banks/:id', eventKey: 'financialsBanks', label: 'Financials & Banks' },
        { to: '/country-edit-housing-life/:id', eventKey: 'housingLife', label: 'Housing & Life' },
        { to: '/country-edit-social/:id', eventKey: 'social', label: 'Social' },
        { to: '/country-edit-industries/:id', eventKey: 'industries', label: 'Industries & Carrier' },
    ];

    return (
        <>
            <Sidebar ticketCount={ticketCount} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            {showErrorMessage && (
                                <Alert variant="danger" onClose={() => setShowErrorMessage(false)} dismissible>
                                    <p><strong>Error</strong> - There are items that require your attention</p>
                                </Alert>
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Card>
                                <Card.Body className="pt-0">
                                    <Row>
                                        <Col xs={12}>
                                            <>
                                                <Row>
                                                    <Col>
                                                        <CountryGeneralInfo
                                                            formData={formData}
                                                        />
                                                    </Col>
                                                </Row>
                                                <Row className="mb-5">
                                                    <Col>
                                                        <NavTabs items={countryNavItems} />
                                                    </Col>
                                                </Row>
                                                <Form onSubmit={handleGeneralSubmit}>
                                                    <Row>
                                                        <Col>
                                                            {loading ? (
                                                                <div>Loading...</div>
                                                            ) : (
                                                                <CountryFinancialsBanks
                                                                    formData={formData}
                                                                    handleChange={handleChange}
                                                                    errors={errors}
                                                                    isEditPage={true}
                                                                    handleFinancialsBanksSubmit={handleGeneralSubmit} // ✅ Correct
                                                                />
                                                            )}
                                                        </Col>
                                                    </Row>
                                                </Form>
                                            </>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row className="buttons-row">
                        <Col>
                            <Button variant="outline-secondary" className="ms-3" onClick={() => navigate('/onboarding-country-list')}>
                                Cancel
                            </Button>
                            <Button variant="outline-danger" onClick={handleDeleteCountry}>
                                Delete Country
                            </Button>
                            <Button variant="outline-secondary" onClick={handleExportPdf}>
                                Download Country Report
                            </Button>
                        </Col>
                    </Row>

                </Container>
            </main>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete this country? This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary me-3" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="outline-danger" onClick={confirmDeleteCountry}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
            {/* Saved info Modal */}
            <Modal show={showSavedModal} onHide={() => setShowSavedModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Image
                        src={icSucess}
                        alt=""
                        className="d-block m-auto mb-4"
                    />
                    <h2 className="text-center mb-3">Update successful</h2>
                    <p className="text-center">Financials & Banks Info Updated</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowSavedModal(false)}>OK</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};
                           

export default CountryEditFinancialsBanks;
